import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminSession } from '@/api/admin';
import { useSystemStore } from '@/stores/system';
import type { SessionStateValue } from '@/api/types';

interface NextEvent {
  label: string;
  diffMs: number;
  tone: 'up' | 'warn' | 'down' | 'neutral';
}

/** 计算下一个关键时刻 — §8.4.6 "距离收尾 4h 26m" 风格 */
function computeNext(
  state: SessionStateValue,
  open: { hour: number; minute: number },
  windDown: { hour: number; minute: number },
  liquidation: { hour: number; minute: number },
): NextEvent | null {
  const now = new Date();
  const today = new Date(now);
  const dayMs = 86400_000;

  const at = (h: number, m = 0) => {
    const d = new Date(today);
    d.setHours(h, m, 0, 0);
    if (d.getTime() < now.getTime()) d.setTime(d.getTime() + dayMs);
    return d.getTime() - now.getTime();
  };

  switch (state) {
    case 'OPEN': {
      // 距离收尾(windDown 时:分)
      return { label: '距收尾', diffMs: at(windDown.hour, windDown.minute), tone: 'up' };
    }
    case 'WIND_DOWN': {
      // 距离强平(liquidation 时:分,缺分钟会把 03:00-03:50 误判为"已过"跳到次日)
      return { label: '距强平', diffMs: at(liquidation.hour, liquidation.minute), tone: 'warn' };
    }
    case 'LIQUIDATING':
      return { label: '强平进行中', diffMs: 0, tone: 'down' };
    case 'CLOSED': {
      // 距离次日开仓(open 时:分)
      return { label: '距开仓', diffMs: at(open.hour, open.minute), tone: 'neutral' };
    }
    default:
      return null;
  }
}

function fmtCountdown(ms: number): string {
  if (ms <= 0) return '0m';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  const s = total % 60;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function SessionCountdown() {
  const sessionState = useSystemStore((s) => s.sessionState);
  const sessQ = useQuery({ queryKey: ['admin', 'trading-session'], queryFn: adminSession.get, staleTime: 5 * 60_000 });

  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const next = useMemo(() => {
    const cfg = sessQ.data;
    return cfg
      ? computeNext(
          sessionState,
          { hour: cfg.openHour, minute: cfg.openMinute ?? 0 },
          { hour: cfg.windDownHour, minute: cfg.windDownMinute ?? 0 },
          // liquidationHour 现为必填;liquidationMinute 缺省回退后端默认 50(强平默认 xx:50),非 0。
          { hour: cfg.liquidationHour, minute: cfg.liquidationMinute ?? 50 },
        )
      : null;
    // 依赖 setTick 触发重算 → useState 已驱动
  }, [sessionState, sessQ.data]);

  if (!next) return null;
  const cls = { up: 'text-up-dim', warn: 'text-warn-dim', down: 'text-down-dim', neutral: 'text-ink-2' }[next.tone];

  return (
    <span className={'hidden md:inline text-xs num ' + cls} title="基于交易时段配置 + 当前 session 状态计算">
      {next.label} {fmtCountdown(next.diffMs)}
    </span>
  );
}
